/**
 * The words on the export / import screens (DECISIONS D-020).
 *
 * Pure, and separate from the components for the reason the rest of src/ui/state
 * is: on a feature whose entire job is moving a day's work between two places
 * that cannot see each other, the SENTENCES are the feature. A surveyor deciding
 * whether to replace what is on the phone with what is in a file is making the
 * most consequential choice this app offers, and they make it from these strings.
 *
 * Three rules run through all of it:
 *
 *   Say the size BEFORE the button. A file that turns out to be 200 MB after
 *   the fact is the app's fault; a file the surveyor was told about is theirs.
 *
 *   Never call a duplicate a problem. Two copies of a survey is the NORMAL
 *   result of the bridge working — you exported from Safari and imported into
 *   the app — and the only wrong move is to overwrite one without asking.
 *
 *   A refusal names the file, not the surveyor. "That is not an UpSurv backup
 *   file" is a fact about a file; "invalid input" is an accusation.
 */

import type { ImportOutcome, ImportPlan, ImportPlanItem, TransferEstimate, TransferProblem } from '../../db/transfer';

/* ═════════════════════════════════ sizes ═════════════════════════════════ */

const KB = 1024;
const MB = 1024 * 1024;

/**
 * A size a person reads, not a number a computer prints. Two significant digits
 * at most: "about 34 MB" is the whole of what anyone needs, and "33.87 MB"
 * invites a precision the estimate does not have.
 */
export function formatBytes(bytes: number): string {
  if (bytes < KB) return `${String(Math.max(0, Math.round(bytes)))} bytes`;
  if (bytes < MB) return `${String(Math.round(bytes / KB))} KB`;
  const mb = bytes / MB;
  return mb < 10 ? `${mb.toFixed(1)} MB` : `${String(Math.round(mb))} MB`;
}

/**
 * Where "this is a big file" starts.
 *
 * Not a cap — nothing is refused and nothing is split. A cap would mean deciding
 * for the surveyor which photos are worth rescuing, and the photos ARE the
 * survey. What the number buys is a sentence: over this, the screen warns that
 * saving will take a moment and needs room on the phone, so a long pause reads
 * as expected rather than as a hang. The container format is streamed both ways
 * (see src/db/transfer.ts), so size costs time and disk, never memory.
 */
export const LARGE_TRANSFER_BYTES = 50 * MB;

function plural(count: number, word: string): string {
  return `${String(count)} ${word}${count === 1 ? '' : 's'}`;
}

/* ════════════════════════════════ exporting ══════════════════════════════ */

export interface ExportView {
  /** The size, said before the tap. Null when there is nothing to save. */
  sizeLine: string | null;
  actionLabel: string;
  /** Present only over `LARGE_TRANSFER_BYTES`. */
  sizeWarning: string | null;
  /** Photos whose bytes have already gone to the office and been cleared. */
  photoNote: string | null;
}

export interface ExportViewInput {
  scope: 'one' | 'device';
  estimate: TransferEstimate;
}

export function exportView({ scope, estimate }: ExportViewInput): ExportView {
  const one = scope === 'one';

  // The lead paragraphs and the empty-state note retired with D-029 R5 —
  // every mount is the compact button now. What is left is what a tap needs:
  // the size facts and the label.
  if (estimate.surveys === 0) {
    return {
      sizeLine: null,
      actionLabel: one ? 'Save a copy' : 'Save a backup file',
      sizeWarning: null,
      photoNote: null,
    };
  }

  const sizeLine =
    `About ${formatBytes(estimate.totalBytes)} — ` +
    (one
      ? `${plural(estimate.photos, 'photo')}.`
      : `${plural(estimate.surveys, 'survey')} and ${plural(estimate.photos, 'photo')}.`);

  return {
    sizeLine,
    actionLabel: one ? 'Save a copy of this survey' : 'Save a backup of everything',
    sizeWarning:
      estimate.totalBytes >= LARGE_TRANSFER_BYTES
        ? `This is a big file — about ${formatBytes(estimate.totalBytes)}. It can take a minute ` +
          'to save, and the phone needs that much room free. Nothing is left out.'
        : null,
    photoNote:
      estimate.photosWithoutBytes > 0
        ? `${plural(estimate.photosWithoutBytes, 'photo')} already went to the office and ` +
          'the picture is no longer kept on this phone, so it is not in the file. Everything ' +
          'else is.'
        : null,
  };
}

/* ════════════════════════════════ refusals ═══════════════════════════════ */

export interface ProblemView {
  headline: string;
  detail: string;
  /** What to do next. Always something; a dead end is not an answer. */
  next: string;
}

/**
 * Why a file was refused, in the surveyor's words.
 *
 * Nothing was written when any of these fire — `readTransfer` validates the
 * whole file before `applyTransfer` is ever called — so every one of them can
 * end with "nothing on this phone has changed", and that sentence is true.
 */
export function problemView(problem: TransferProblem): ProblemView {
  switch (problem.kind) {
    case 'not-an-export':
      return {
        headline: 'That is not an UpSurv backup file.',
        detail: 'Backup files are made by this app and their names end in “.upsurv”.',
        next: 'Nothing on this phone has changed. Pick the file again, or ask the office.',
      };
    case 'truncated':
      return {
        headline: 'That backup file is incomplete.',
        detail:
          'Part of it is missing — that usually means the copy or the download stopped ' +
          'partway through.',
        next: 'Nothing on this phone has changed. Save the backup again and use the new file.',
      };
    case 'damaged':
      return {
        headline: 'That backup file could not be read.',
        detail: 'It is an UpSurv file, but something in it is not readable.',
        next: 'Nothing on this phone has changed. Save the backup again, or send this file to the office.',
      };
    case 'unsupported':
      return {
        headline: 'That backup was made by a newer version of UpSurv.',
        detail: 'This app does not know how to read it yet.',
        next: 'Nothing on this phone has changed. Update the app, then open the file again.',
      };
    case 'invalid-document':
      return {
        headline: 'That backup file holds a survey this app cannot read.',
        detail: problem.detail,
        next: 'Nothing on this phone has changed — none of the file was brought in. Send it to the office.',
      };
    case 'empty':
      return {
        headline: 'That backup file has no surveys in it.',
        detail: 'It was saved from a device that had nothing on it.',
        next: 'Nothing on this phone has changed. Pick a different file.',
      };
  }
}

/* ════════════════════════════════ importing ══════════════════════════════ */

/** "6 Aug 2026" — from the ISO string itself, so it reads the same everywhere. */
export function formatDay(iso: string): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!match) return 'an unknown date';
  const [, year, month, day] = match;
  const name = months[Number(month) - 1];
  if (name === undefined) return 'an unknown date';
  return `${String(Number(day))} ${name} ${String(year)}`;
}

export interface ImportItemView {
  surveyId: string;
  /** The survey, named the way the list names it. */
  title: string;
  /** What this line is offering, in one sentence. */
  line: string;
  /** True when the surveyor has a real choice to make about this one. */
  choosable: boolean;
  /** Whether it starts chosen. New surveys do; duplicates never do. */
  defaultChosen: boolean;
  /** The label on its own control. */
  choiceLabel: string;
}

export function importItemView(item: ImportPlanItem): ImportItemView {
  const name = item.title;
  const photos = item.photos === 0 ? 'no photos' : plural(item.photos, 'photo');

  if (item.disposition === 'discarded') {
    return {
      surveyId: item.surveyId,
      title: name,
      line:
        `You discarded ${name} on this phone and the office has not been told yet, so it ` +
        'cannot be brought back on here. Once this phone has had signal, try again.',
      choosable: false,
      defaultChosen: false,
      choiceLabel: '',
    };
  }

  if (item.disposition === 'duplicate') {
    /*
     * Which copy is newer, said plainly — because that is the only fact the
     * surveyor can actually decide on, and they cannot see either date from
     * here. The two `updatedAt` stamps are compared, not the file's save time:
     * a backup taken today of a survey nobody has touched since Tuesday is a
     * Tuesday survey, and calling it "the more recent one" would be a lie that
     * costs a day's work.
     */
    const here = item.existing?.updatedAt ?? '';
    const there = item.fileUpdatedAt;
    const fileWhen = formatDay(there);
    const hereWhen = here === '' ? '' : formatDay(here);
    const which =
      here === ''
        ? ''
        : here > there
          ? ` The copy already here was worked on more recently (${hereWhen}) than the one in ` +
            `the file (${fileWhen}).`
          : here === there
            ? ' Both copies were last worked on at the same moment.'
            : ` The copy in the file is the more recent one (${fileWhen}); the one here was last ` +
              `worked on ${hereWhen}.`;
    return {
      surveyId: item.surveyId,
      title: name,
      line: `${name} is already on this phone.${which} Bringing it in replaces what is here.`,
      choosable: true,
      // NEVER pre-selected. Replacing is the one act in this feature that can
      // destroy work, so it is always a deliberate tap.
      defaultChosen: false,
      choiceLabel: `Replace ${name} with the copy in the file`,
    };
  }

  return {
    surveyId: item.surveyId,
    title: name,
    line: `${name} — ${photos}. Not on this phone yet.`,
    choosable: true,
    defaultChosen: true,
    choiceLabel: `Bring in ${name}`,
  };
}

export interface ImportPlanView {
  headline: string;
  lead: string;
  items: ImportItemView[];
  /** The button, or null when there is nothing this file can add. */
  actionLabel: string | null;
  /** Said when every survey in the file is already here. */
  nothingToDo: string | null;
}

export function importPlanView(plan: ImportPlan): ImportPlanView {
  const items = plan.items.map((item) => importItemView(item));
  const fresh = plan.items.filter((item) => item.disposition === 'new').length;
  const duplicates = plan.items.filter((item) => item.disposition === 'duplicate').length;
  const usable = items.filter((item) => item.choosable).length;

  const headline =
    plan.items.length === 1
      ? `This file holds one survey, saved ${formatDay(plan.exportedAt)}.`
      : `This file holds ${plural(plan.items.length, 'survey')}, saved ${formatDay(plan.exportedAt)}.`;

  const lead =
    duplicates === 0
      ? 'Check the list, then bring them in. Everything comes with its answers, notes and photos.'
      : 'Anything already on this phone is left alone unless you say otherwise — tick it to ' +
        'replace what is here with the copy in the file.';

  return {
    headline,
    lead,
    items,
    actionLabel: usable === 0 ? null : fresh > 0 || duplicates > 0 ? 'Bring these in' : null,
    nothingToDo:
      usable === 0
        ? 'Everything in this file is already on this phone, or is waiting to be withdrawn from ' +
          'the office. There is nothing to bring in.'
        : null,
  };
}

/**
 * What happened, once it has. Names numbers, because a silent success is a
 * doubt — and names a survey the phone could not write, with the error's own
 * words, because since the fix wave (D-040 postscript) one refused survey no
 * longer sinks the file, and a sentence that says "2 surveys" about a file
 * of three has to say where the third went.
 */
export function importOutcomeMessage(outcome: ImportOutcome): string {
  const refusals = outcome.refused.map((item) => item.message).join(' ');
  if (outcome.imported.length === 0) {
    const base = 'Nothing was brought in. This phone is unchanged.';
    if (outcome.refused.length === 0) return base;
    return `${base} ${plural(outcome.refused.length, 'survey')} could not be written: ${refusals}.`;
  }
  const surveys = plural(outcome.imported.length, 'survey');
  const photos = outcome.photos === 0 ? 'no photos' : plural(outcome.photos, 'photo');
  const landed =
    `${surveys} and ${photos} are now on this phone. They are in My Surveys, and they will sync ` +
    'and submit like any other.';
  if (outcome.refused.length === 0) return landed;
  return (
    `${landed} ${plural(outcome.refused.length, 'survey')} could not be written and ` +
    `${outcome.refused.length === 1 ? 'is' : 'are'} not on this phone: ${refusals}.`
  );
}
